/**
 * Pure idempotency + amount-derivation orchestration for the checkout page.
 *
 * These helpers were extracted verbatim from `CheckoutPage.tsx` so the
 * payment-safety logic can be unit-tested without mounting React. None of the
 * behavior changed: `CheckoutPage` now calls these functions, passing in the
 * `useRef`-backed idempotency map and the shared `generateIdempotencyKey`.
 *
 * Why this matters:
 *   - The idempotency key MUST be HELD across retries of the same intent so a
 *     lost-response resubmit dedupes server-side and can never double-charge.
 *   - It MUST ROTATE on a DEFINITIVE decline so a corrected retry isn't replayed
 *     against the cached terminal `declined`.
 *   - It MUST be backed by sessionStorage so a mid-payment page reload reuses the
 *     same key (POS Hive parity).
 */

import { percentageTipCents, toCents } from '@order-up/common';

/** Per-intent idempotency entry: the signature it was minted for + the key. */
export interface IdempotencyEntry {
    signature: string;
    key: string;
}

/** sessionStorage key under which a scope's idempotency entry is persisted. */
export const idempotencyStorageKey = (scope: string): string =>
    `orderup.idempotency.${scope}`;

/**
 * Read the persisted idempotency entry for `scope`, but ONLY return it when its
 * stored signature matches the current intent's signature. A signature mismatch
 * (order changed) or corrupt/unavailable storage yields null so a fresh key is
 * minted.
 */
export function readPersistedIdempotency(
    scope: string,
    signature: string,
): IdempotencyEntry | null {
    try {
        const raw = sessionStorage.getItem(idempotencyStorageKey(scope));
        if (!raw) return null;
        const parsed = JSON.parse(raw) as { signature?: string; key?: string };
        if (parsed?.signature === signature && typeof parsed.key === 'string') {
            return { signature: parsed.signature, key: parsed.key };
        }
    } catch {
        /* storage unavailable or corrupt — fall back to a freshly minted key */
    }
    return null;
}

/** Persist `entry` for `scope`; swallow storage errors (private mode / quota). */
export function writePersistedIdempotency(
    scope: string,
    entry: IdempotencyEntry,
): void {
    try {
        sessionStorage.setItem(
            idempotencyStorageKey(scope),
            JSON.stringify(entry),
        );
    } catch {
        /* storage unavailable (private mode / quota) — in-memory ref still holds */
    }
}

/**
 * Resolve the idempotency key for `scope` against the intent `signature`.
 *
 * Resolution order (all behavior-preserving from CheckoutPage):
 *   1. In-memory hit for the SAME signature → reuse (held across retries).
 *   2. Miss / signature changed → try the sessionStorage-persisted entry
 *      (survives a page reload), else mint a fresh key via `generateKey`.
 *   3. Cache the resolved entry both in-memory AND in sessionStorage.
 *
 * @param refs       the mutable in-memory map (CheckoutPage passes `ref.current`)
 * @param generateKey the shared key minter (`generateIdempotencyKey`)
 */
export function resolveIdempotencyKey(
    refs: Record<string, IdempotencyEntry>,
    generateKey: (scope: string, context?: string) => string,
    scope: string,
    signature: string,
    context?: string,
): string {
    let entry = refs[scope];
    if (!entry || entry.signature !== signature) {
        entry =
            readPersistedIdempotency(scope, signature) ?? {
                signature,
                key: generateKey(scope, context),
            };
        refs[scope] = entry;
        writePersistedIdempotency(scope, entry);
    }
    return entry.key;
}

/** Drop the cached key for `scope` (in-memory + sessionStorage) → next call mints fresh. */
export function resetIdempotencyKey(
    refs: Record<string, IdempotencyEntry>,
    scope: string,
): void {
    delete refs[scope];
    try {
        sessionStorage.removeItem(idempotencyStorageKey(scope));
    } catch {
        /* storage unavailable — in-memory delete above already applied */
    }
}

/**
 * True when a thrown API error means the charge DEFINITIVELY did not land, so
 * the key can be safely rotated. A 4xx that is NOT 409 is a definitive
 * client/processor rejection. A missing status (network/timeout), 409
 * (in-flight), or 5xx (ambiguous) all KEEP the key so a resubmit is deduped.
 */
export function isDefinitiveChargeFailure(error: unknown): boolean {
    const status = (error as { status?: number } | null)?.status;
    return (
        typeof status === 'number' &&
        status >= 400 &&
        status < 500 &&
        status !== 409
    );
}

/**
 * Build the intent signature for the checkout payment scope. A genuinely
 * changed order (different cart / total / tip) yields a different signature and
 * therefore a fresh key.
 *
 * A change to the tip RULE (e.g. the move to `percentageTipCents`, which can
 * move a preset tip by 1¢) or to the tip BASE changes `tipAmount`, so it
 * rotates the key too — a guest who retries across such a change gets a fresh
 * key. The idempotency key is therefore NOT what stops a double charge there:
 * the cart-conversion guard is. The server refuses checkout on a cart that has
 * already become an order (`CART_ALREADY_CONVERTED`, routes/orders.ts), and
 * this page looks up an existing order for the cart before paying.
 */
export function checkoutSignature(
    cartId: string | number,
    total: number,
    tipAmount: number,
): string {
    return `${cartId}:${total}:${tipAmount}`;
}

/**
 * THE base a percentage tip is taken from, in dollars: the SERVER-priced
 * subtotal (the preview's) once it exists, the cart's before then.
 *
 * The server reprices every line authoritatively at preview and at checkout,
 * so the preview subtotal is the one the guest is shown and the one the order
 * is built from. Taking the tip from the cart's figure while the tiles showed
 * the preview's meant a repriced cart offered one amount and charged another.
 */
export function resolveTipBaseDollars(
    previewSubtotal: number | null | undefined,
    cartSubtotal: number | null | undefined,
): number {
    if (typeof previewSubtotal === 'number' && Number.isFinite(previewSubtotal)) {
        return previewSubtotal;
    }
    return typeof cartSubtotal === 'number' && Number.isFinite(cartSubtotal) ? cartSubtotal : 0;
}

/**
 * THE preset tip, in whole cents, that `percent` (percentage points) offers on
 * `baseAmount` dollars — the till's rule (`percentageTipCents`, half away from
 * zero). The tip tiles, a typed custom percent and the charge
 * (`deriveTipAmount`) all call this with the same base, so the amount shown is
 * the amount charged.
 */
export function presetTipCents(baseAmount: number, percent: number): number {
    if (!Number.isFinite(percent) || percent <= 0) return 0;
    return percentageTipCents(toCents(baseAmount), percent);
}

/**
 * Derive the tip in dollars from the UI selection.
 *   - test mode → 0
 *   - a custom-tip string → its parsed value (NaN → 0)
 *   - else a positive percentage of the base amount, in whole cents by
 *     `presetTipCents` — the same call the tip tiles make
 *   - otherwise → 0
 *
 * `baseAmount` must be `resolveTipBaseDollars(preview?.subtotal, cart?.subtotal)`.
 */
export function deriveTipAmount(opts: {
    isTestMode: boolean;
    baseAmount: number;
    selectedTip: number;
    customTip: string;
}): number {
    const { isTestMode, baseAmount, selectedTip, customTip } = opts;
    if (isTestMode) return 0;
    // Whole cents, as the API rounds it — a fractional tip is a total the
    // server will not charge.
    if (customTip) {
        const customTipValue = parseFloat(customTip);
        return isNaN(customTipValue) ? 0 : toCents(customTipValue) / 100;
    }
    if (selectedTip > 0) {
        // Whole cents by the till's rule (half away from zero), not the raw
        // dollar product: 1.50 × 15% is 0.22499… in binary, which the charge
        // route's Math.round(tip × 100) made 22¢ while the till charges 23¢.
        return presetTipCents(baseAmount, selectedTip) / 100;
    }
    return 0;
}
